import { useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { TrendingUp, Euro, Target, BarChart3 } from "lucide-react";

export default function OperatorROICalculator() {
  const { t } = useTranslation();
  const [leadsPerMonth, setLeadsPerMonth] = useState(10);
  const [avgLeadPrice, setAvgLeadPrice] = useState(150);
  const [conversionRate, setConversionRate] = useState(30);
  const [avgDealValue, setAvgDealValue] = useState(5000);
  const [currentCAC, setCurrentCAC] = useState(2500);

  const monthlyCost = leadsPerMonth * avgLeadPrice;
  const expectedDeals = (leadsPerMonth * conversionRate) / 100;
  const expectedRevenue = expectedDeals * avgDealValue;
  const roi = monthlyCost > 0 ? ((expectedRevenue - monthlyCost) / monthlyCost) * 100 : 0;
  const craftfindusCAC = expectedDeals > 0 ? monthlyCost / expectedDeals : 0;
  const cacSavings = currentCAC > 0 ? ((currentCAC - craftfindusCAC) / currentCAC) * 100 : 0;
  const yearlySavings = expectedDeals > 0 ? (currentCAC - craftfindusCAC) * expectedDeals * 12 : 0;

  return (
    <OperatorLayout title={t("op.roiTitle")} subtitle={t("op.roiSubtitle")}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">{t("op.roiParams")}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between"><Label>{t("op.roiLeadsPerMonth")}</Label><span className="text-sm font-medium">{leadsPerMonth}</span></div>
                <Slider value={[leadsPerMonth]} onValueChange={([v]) => setLeadsPerMonth(v)} min={1} max={50} step={1} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between"><Label>{t("op.roiAvgLeadPrice")}</Label><span className="text-sm font-medium">{avgLeadPrice} €</span></div>
                <Slider value={[avgLeadPrice]} onValueChange={([v]) => setAvgLeadPrice(v)} min={39} max={399} step={10} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between"><Label>{t("op.roiConversion")}</Label><span className="text-sm font-medium">{conversionRate} %</span></div>
                <Slider value={[conversionRate]} onValueChange={([v]) => setConversionRate(v)} min={5} max={80} step={5} />
              </div>
              <div className="space-y-2">
                <Label>{t("op.roiAvgDealValue")}</Label>
                <Input type="number" value={avgDealValue} onChange={(e) => setAvgDealValue(Number(e.target.value) || 0)} min={0} />
              </div>
              <div className="space-y-2">
                <Label>{t("op.roiCurrentCAC")}</Label>
                <Input type="number" value={currentCAC} onChange={(e) => setCurrentCAC(Number(e.target.value) || 0)} min={0} />
                <p className="text-micro text-muted-foreground">{t("op.roiIndustryAvg")}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">{t("op.roiScenarios")}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { label: t("op.roiScenarioFreelancer"), leads: 3, price: 149, conv: 35, deal: 8000 },
                  { label: t("op.roiScenarioMedium"), leads: 10, price: 150, conv: 30, deal: 5000 },
                  { label: t("op.roiScenarioLarge"), leads: 20, price: 199, conv: 25, deal: 12000 },
                ].map((s) => (
                  <button key={s.label} className="w-full text-left px-4 py-3 border border-border rounded-md hover:bg-muted/50 transition-colors text-sm"
                    onClick={() => { setLeadsPerMonth(s.leads); setAvgLeadPrice(s.price); setConversionRate(s.conv); setAvgDealValue(s.deal); }}>
                    <span className="font-medium">{s.label}</span>
                    <span className="text-muted-foreground ml-2">— {s.leads} Leads × {s.price} € × {s.conv}% × {s.deal.toLocaleString("de-DE")} €</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Card><CardContent className="p-4 text-center"><Euro className="w-5 h-5 text-muted-foreground mx-auto mb-2" /><p className="text-micro mb-1">{t("op.roiMonthlyCost")}</p><p className="text-2xl font-medium">{monthlyCost.toLocaleString("de-DE")} €</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><Target className="w-5 h-5 text-muted-foreground mx-auto mb-2" /><p className="text-micro mb-1">{t("op.roiExpectedCustomers")}</p><p className="text-2xl font-medium">{expectedDeals.toFixed(1)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><TrendingUp className="w-5 h-5 text-muted-foreground mx-auto mb-2" /><p className="text-micro mb-1">{t("op.roiExpectedRevenue")}</p><p className="text-2xl font-medium">{expectedRevenue.toLocaleString("de-DE")} €</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><BarChart3 className="w-5 h-5 text-muted-foreground mx-auto mb-2" /><p className="text-micro mb-1">{t("op.roiLabel")}</p><p className={`text-2xl font-medium ${roi > 0 ? "text-accent" : "text-destructive"}`}>{roi.toFixed(0)} %</p></CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">{t("op.roiCACComparison")}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between"><span className="text-sm">{t("op.roiCurrentCACLabel")}</span><span className="font-medium">{currentCAC.toLocaleString("de-DE")} €</span></div>
              <div className="flex items-center justify-between"><span className="text-sm">{t("op.roiCraftfindusCAC")}</span><span className="font-medium text-accent">{craftfindusCAC.toLocaleString("de-DE", { maximumFractionDigits: 0 })} €</span></div>
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between"><span className="text-sm font-medium">{t("op.roiSavingsPerCustomer")}</span><span className="font-medium text-accent">{cacSavings > 0 ? `${cacSavings.toFixed(0)} %` : "—"}</span></div>
              </div>
              <div className="space-y-2 pt-2">
                <div><p className="text-micro mb-1">{t("op.roiCurrent")}</p><div className="h-6 bg-destructive/20 rounded-sm relative"><div className="h-full bg-destructive/60 rounded-sm" style={{ width: "100%" }} /><span className="absolute inset-0 flex items-center justify-center text-xs font-medium">{currentCAC.toLocaleString("de-DE")} €</span></div></div>
                <div><p className="text-micro mb-1">{t("op.roiWithCraftfindus")}</p><div className="h-6 bg-accent/20 rounded-sm relative"><div className="h-full bg-accent/60 rounded-sm" style={{ width: `${currentCAC > 0 ? Math.min((craftfindusCAC / currentCAC) * 100, 100) : 0}%` }} /><span className="absolute inset-0 flex items-center justify-center text-xs font-medium">{craftfindusCAC.toLocaleString("de-DE", { maximumFractionDigits: 0 })} €</span></div></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-accent">
            <CardContent className="p-6 text-center">
              <p className="text-micro mb-2">{t("op.roiAnnualSavings")}</p>
              <p className="text-display text-accent">{yearlySavings > 0 ? `${yearlySavings.toLocaleString("de-DE", { maximumFractionDigits: 0 })} €` : "—"}</p>
              <p className="text-micro text-muted-foreground mt-2">{t("op.roiBasedOn", { count: Number((expectedDeals * 12).toFixed(0)) })}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">{t("op.roiTalkingPoint")}</CardTitle></CardHeader>
            <CardContent>
              <blockquote className="border-l-4 border-accent pl-4 text-sm text-muted-foreground italic">
                „{t("op.roiTalkingPointText", { leads: leadsPerMonth, price: avgLeadPrice, cost: monthlyCost.toLocaleString(), conv: conversionRate, deal: avgDealValue.toLocaleString(), revenue: expectedRevenue.toLocaleString(), roi: roi.toFixed(0) })}"
              </blockquote>
            </CardContent>
          </Card>
        </div>
      </div>
    </OperatorLayout>
  );
}
